/**
 * Base file for all ticketing stuff
 * Contains MX enums and structures, typedefs and helpers.
 *
 * @module ticketing/public
 */

import { h } from 'preact';
import { isObject, accessNested, joinPaths, pathOnly } from 'utils/utils';
import Store from 'store/store';
import { getRelativeRoot } from 'components/core/link/link';

const IS_DEMO = typeof window !== 'undefined' && accessNested(window, '__INITIAL_STATE__.' +
	'appData.options.isDemoTicketing', false);

const IFRAME_COMMUNICATOR_PAGE_SLUG = 'iframe-communicator';
const PRINT_YOUR_TICKET_PAGE_SLUG = 'print-your-ticket';

/** Fallback deeplinks providers (ordered by preference) */
const deeplinkProviders = ['cinema', 'fandango', 'movietickets', 'atom'];

/**
 * Return whether ticketing is in demo mode.
 * Demo ticketing does use test data.
 *
 * @function
 * @return {!boolean} true if demo ticketing is enabled, false otherwise
 */
export const isDemoTicketing = () => IS_DEMO;

/**
 * Available gateway identifiers
 * If you add a gateway here, then every exhibitor using this gateway will have direct
 * ticketing enable on the platform.
 *
 * @readonly
 * @enum {string}
 */
export const gateways = {
	AUTHNET:    'authnet',
	VANTIV:     'vantiv',
	PXPAY:      'pxpay',
	VERIFONE2:  'verifone2',
	BRAINTREE:  'braintree',
	MASTERCARD: 'mastercard'
	// PAYEEZY:   'payeezy'
};

/**
 * Payment gateway configurations.
 * For each payment gateway, the configuration of the payments.
 *
 * @readonly
 */
export const gatewayConfigs = {
	authnet: {
		useIframeComm: true
	},
	vantiv: {
		useIframeComm: true
	},
	pxpay: {
		useIframeComm: true
	},
	verifone2: {
		useIframeComm: true
	},
	braintree: {
		useIframeComm: false
	},
	mastercard: {
		useIframeComm: false
	}
	// payeezy: {
	// 	useIframeComm: true
	// }
};

/**
 * Temporary default gateway for ticketing.
 * As the gateway is not available in the doc yet, you can use this for test purposes.
 *
 * @type {string}
 */
export const defaultGateway = gateways.AUTHNET;

/**
 * Indicate the current status of the seat
 *
 * @readonly
 * @enum {number}
 * @see {@link https://bit.ly/2GKuww5|MX Ticketing Documentation}
 */
export const seatStatus = {
	AVAILABLE:   0,
	SOLD:        1,
	HOUSE:       2,
	WHEELCHAIR:  3,
	RESERVED:    4,
	BROKEN:      5,
	PLACEHOLDER: 6,
	COMPANION:   7
};

/**
 * Enumeration of MX errors.
 * Can be used for localisation.
 *
 * @readonly
 * @enum {number}
 * @see {@link https://goo.gl/AuhR9D|MX Documentation}
 */
export const mxErrors = {
	UNEXPECTED_ERROR:                       10000, // An unexpected error/database error/component error has occurred
	CINEMA_CHAIN_CONFIG_ISSUE:              10001, // There's a configuration issue with the cinema chain requested
	AUTHORISATION_EXCEPTION:                10002, // An internal error occurred while trying to authorise the request
	VENDOR_CONFIGURATION_EXCEPTION:         10003, // There's a configuration issue with the authenticated vendor

	CINEMA_TICKET_SERVICE_ACCESS_ERROR:     20000, // Unable to reach the cinema's ticketing service
	CINEMA_TICKET_SERVICE_TIMEOUT:          20001, // Cinema’s ticketing service failed to respond in a timely manner
	CANT_CONNECT_TO_SITE:                   20002, // The Cinema’s ticketing service failed to connect to the specified site
	CANT_CONNECT_TO_LOYALTY:                20003, // The cinema’s ticketing service failed to connect to its loyalty system

	UNEXPECTED_CINEMA_TICKET_SERVICE_ERROR: 30000, // An unexpected error occurred at the cinema’s ticketing service
	FAILED_TO_RETRIEVE_SEATS_FOR_ORDER:     30001, // Seats were booked, but an unexpected error occurred while trying to retrieve seat information. Try to retrieve the order again
	FAILED_TO_PROCESS_ORDER_VALUE:          30002, // An unexpected error occurred while trying to calculate the order total. The order was cancelled
	REFUND_BOOKING_FAILED:                  30003, // The cinema's ticketing service was unable to refund this booking.

	CINEMA_TICKET_SERVICE_CONFIG_ERROR:     31001, // The cinema’s ticketing service has not been correctly configured with MXTransact
	CINEMA_TICKET_SERVICE_NOT_FOUND:        31002, // A site existing in MXTransact does not exist at cinema

	BOOKING_FEE_CONFIG_ERROR:               32001, // There’s a problem with the cinema’s ticketing service booking fee configuration
	PAYMENT_CONFIG_ERROR:                   32002, // There’s a problem with the cinema’s ticketing service payment configuration

	CINEMA_CHAIN_NOT_FOUND:                 40001, // The requested cinema chain cannot be found
	SITE_NOT_FOUND:                         40002, // The specified site cannot be found
	SHOWTIME_NOT_FOUND:                     40003, // The specified Showtime cannot be found
	TICKET_TYPES_MANDATORY:                 40004, // The client must provide the ticket types for the request.
	CONCESSIONS_NOT_SUPPORTED:              40005, // An attempt to purchase concessions was made, but Concessions are not supported in the API
	SHOWTIME_DOES_NOT_SUPPORT_SEATING:      40006, // An attempt to get screen layout was made, however, the Showtime is not configured to be seat-allocated
	TOTAL_PAYMENT_AMOUNT_IS_INSUFFICIENT:   40007, // The total payment amount specified doesn’t match the order total
	MULTI_SITE_ORDER_NOT_SUPPORTED:         40008, // A single order cannot contain sessions at more than one site
	ORDER_DOES_NOT_EXISTS:                  40009, // Could not find an order with the specified ID
	NO_SEAT_MAP_FOR_SOLD_OUT_SHOWTIMES:     40010, // Seat map data was requested for a showtime that is already sold out
	TICKET_TYPE_NOT_FOUND:                  40011, // The requested ticket type was not found for the showtime
	REQUEST_TOO_LARGE:                      40012, // The request was too large.
	CUSTOMER_DETAILS_NOT_SET:               40013, // Customer details were not set for the order.
	VENDOR_ACCESS_NOT_ALLOWED:              40014, // You have no access to the Cinema Chain requested.
	REFUND_PAST_SCREENINGS_NOT_ALLOWED:     40015, // Cannot refund a booking when one or more tickets that are part of the booking are for screenings that have already screened.
	REFUND_CINEMA_CHAIN_NOT_ALLOWED:        40016, // The cinema chain does not allow refunds
	BOOKINS_BELONGS_TO_ANOTHER_VEND0R:      40017, // Cannot refund a booking made through a different vendor.
	INSUFFICIENT_CUSTOMER_DETAILS:          40018, // An email, name and/or phone number must be provided.
	LOYALTY_NOT_ALLOWED_FOR_CINEMA_CHAIN:   40019, // The cinema chain does not allow loyalty.
	INSUFFICIENT_LOYALTY_MEMBER_DETAILS:    40020, // Insufficient loyalty membership details.
	TICKET_TYPES_FOR_PAST_SCREENINGS:       40021, // Ticket types not available for past screenings.

	SEATS_UNAVAILABLE:                      41001, // The requested seats are not available
	NO_CONTIGUOUS_SEATS:                    41002, // The selected seats could not be automatically allocated in a single row
	BOOKING_DOES_NOT_EXIST:                 41003, // A booking with the specified ID does not exist
	BOOKING_ALREADY_CANCELLED:              41004, // The specified booking has already been cancelled
	SHOWTIME_UNAVAILABLE:                   41005, // The requested showtime is not available at the cinema’s ticketing service
	TICKET_NOT_FOR_SALE:                    41006, // Selected tickets are not currently for sale for this showtime
	SELECTED_SEATS_INVALID_FOR_THE_TICKET:  41007, // Selected seats are not valid for the tickets in the order
	ORDER_ALREADY_BEING_COMPLETED:          41008, // A request to complete the order was already made and is currently in progress.
	ORDER_CANNOT_BE_UNIQUELY_IDENTIFIED:    41009, // The cinema's ticketing service was not able to identify the requested order
	COULD_NOT_GET_TICKET_TYPES_FOR_SESSION: 41010, // Could not get ticket types for the requested session. Please make sure the session is still available
	LOYALTY_MEMBER_NOT_FOUND:               41011  // Loyalty member could not be found.
};

/**
 * Enumeration of Powster ticketing server errors.
 * Can be used for localisation.
 *
 * @readonly
 * @enum {number}
 * @see {@link https://goo.gl/55yo3T|Powster Ticketing Error codes}
 */
export const powErrors = {
	INVALID_API_KEY:         101, // Invalid API developer key
	AUTH_FAIL:               102, // Authentication failed
	AUTH_EXPIRED:            103, // Authentication expired

	INVALID_EXHIBITOR:       301, // Unknown or incorrect exhibitor

	INVALID_ORDER:           501, // The order structure returned by MX is not valid
	NO_ORDER:                502, // The provided order does not exist in the ticketing database
	COMPLETE_ORDER_FAIL:     503, // Failed to complete the order and get a proper booking reference
	NO_BOOKING_REF:          504, // The provided order does not have any booking reference
	NOT_YOUR_ORDER:          507, // You don't have access to this order
	PRIVATE_SCREENING:       508, // You don't have access to this screening
	TOO_MANY_ORDERS:         509, // Number of bookings for promotion exceeded
	TOO_MANY_TICKETS:        510, // Number of available tickets for promotion exceeded

	TRANSACTION_FAIL:        601, // The transaction was rejected
	NO_MERCHANT:             602, // The merchant does not exist
	TRANSACTION_VOID_FAIL:   603, // The given transaction cannot be voided
	NO_MERCHANT_CREDENTIALS: 604, // Could not acquire merchant credentials for the given site

	INVALID_PRICE:           901, // The price returned by MX is not a valid amount
	INVALID_BOOKING_FEE:     902  // Incorrect custom booking fee
};

/**
 * Return the key associated with a MX error code for localisation.
 *
 * @function
 * @param {!number} code - the MX error code
 * @return {string} the key associated with the given error id, or undefined if not found
 */
export const getMxErrorKey = (code) => Object.keys(mxErrors).find((key) => mxErrors[key] === code);

/**
 * Return the key associated with a Powster Ticketing error code for localisation.
 *
 * @function
 * @param {!number} code - the Powster Ticketing error code
 * @return {string} the key associated with the given error id, or undefined if not found
 */
export const getPowErrorKey = (code) => Object.keys(powErrors).find((key) => powErrors[key] === code);

/**
 * Return whether the given object is a valid error (as defined in the ticketing API)
 *
 * @param {*} obj - the variable to test
 * @return {!boolean} true if obj is an error object, false otherwise
 */
const isErrorObject = (obj) => {
	if (!isObject(obj) || !isObject(obj.data)) return false;
	return isObject(obj.data.error) && typeof obj.data.from === 'string';
};

/**
 * Return whether the given object is a valid MX error (as defined in the MX API)
 *
 * @param {*} obj - the variable to test
 * @return {!boolean} true if obj is an MX error object, false otherwise
 */
export const isMxErrorObject = (obj) => {
	return isErrorObject(obj) && obj.data.from === 'mx' &&
		typeof obj.data.error.errorCode === 'number' &&
		typeof obj.data.error.errorCodeDescription === 'string';
};

/**
 * Return whether the given object is a valid Powster Ticketing error.
 *
 * @param {*} obj - the variable to test
 * @return {!boolean} true if obj is a Powster Ticketing error object, false otherwise
 */
export const isPowErrorObject = (obj) => {
	return isErrorObject(obj) && obj.data.from === 'pow' && typeof obj.data.error.code === 'number';
};

/**
 * @global
 * @typedef {Object} SeatLayout
 * @property {number} boundaryRight - the rightmost position of the map, in percentage 0-100
 * @property {number} boundaryLeft - the leftmost position of the map, in percentage 0-100
 * @property {number} boundaryTop - the topmost position of the map, in percentage 0-100
 * @property {number} screenStart - the leftmost position of the screen, in percentage 0-100
 * @property {number} screenWidth - the width of the screen, in percentage 0-100
 * @property {Array.<AreaCategory>} areaCategories - list of AreaCategories in the screen layout
 * @property {Array.<Area>} areas - list of Areas in the screen layout
 */

/**
 * @global
 * @typedef {Object} AreaCategory
 * @property {string} areaCategoryCode - the ID of the area category
 * @property {number} seatsToAllocate - number of seats that must be allocated for the area category
 * @property {number} seatsAllocatedCount - number of seats that have been automatically allcoated
 * @property {number} seatsNotAllocatedCount - number of seats that have not been allocated
 * @property {Array.<SeatPosition>} selectedSeats - list of SeatPositions for the allocated seats
 */

/**
 * @global
 * @typedef {Object} SeatPosition
 * @property {number} areaNumber - identifies which area the seat belongs to (1 based)
 * @property {number} rowIndex - the row within the area the seat belongs to (0 based)
 * @property {number} columnIndex - the column within the area the seat belongs to (0 based)
 */

/**
 * @global
 * @typedef {Object} Area
 * @property {number} number - uniquely identifies the area within the seat layout
 * @property {string} areaCategoryCode - the area category code this area belong to
 * @property {number} numberOfSeats - total number of seats within this area
 * @property {number} columnCount - number of columns of seats in the area
 * @property {number} rowCount - number of rows of seats in the area
 * @property {string} description - textual description of the area (e.g. "Premium", "Stalls")
 * @property {number} left - the leftmost position of the area within the screen layout (0-100)
 * @property {number} top - the topmost position of the area within the screen layout (0-100)
 * @property {number} width - the width of the area within the screen layout (0-100)
 * @property {number} height - the height of the area within the screen layout (0-100)
 * @property {Array.<Row>} rows - list of Rows in the area
 */

/**
 * @global
 * @typedef {Object} Row
 * @property {string} rowLabel - the name of the row
 * @property {Array.<Seat>} seats - list of seats in the row
 */

/**
 * @global
 * @typedef {Object} Seat
 * @property {SeatPosition} position - the seat position object
 * @property {Array.<SeatPosition>} seatsInGroup - list of seat positions for grouped seats (sofas)
 * @property {string} seatLabel - name of the seat (commonly rowLable + seatLabel, e.g. "A12")
 * @property {SeatStatus} status - the current seat status
 * @property {SeatStatus} originalStatus - the seat status before being reserved
 */

/**
 * Return whether direct ticketing is enabled for a specific theater.
 *
 * @function
 * @param {Theater} theater - the theater of the active screening
 * @return {boolean} true if direct ticketing is enabled, false otherwise
 */
export const isDirectTicketingEnabled = (() => {
	let store_;

	return (theater) => {
		if (!store_) store_ = Store.get();

		if (!store_.appData.options.enableDirectTicketing) return false;
		if (store_.appData.options.forceDirectTicketing) return true;

		if (window.__IS_FACEBOOK__) return false;

		if (document.location.protocol !== 'https:' && !__IS_DEMO__ && !__IS_LOCAL__) {
			return false;
		}

		const exhibitor = theater.exhibitor;
		if (!exhibitor) return false;

		// Check if ticketing is explicitly disabled in the theater master doc
		let ticketingEnabled = accessNested(theater, 'masterDoc.ticketing.enabled', true);
		if (!ticketingEnabled) return false;

		// Check that ticketing is explicitly enabled in the exhibitor doc
		ticketingEnabled = accessNested(exhibitor, 'doc.ticketing.enabled', false);
		const paymentEnabled = accessNested(exhibitor, 'doc.payment.enabled', false);

		// TODO: Override on theater level
		if (!ticketingEnabled || !paymentEnabled) return false;

		// Make sure the payment gateway is known and supported
		const gateway = accessNested(exhibitor, 'doc.payment.gateway', '');
		if (Object.values(gateways).indexOf(gateway) === -1) return false;

		// TEMP: Disable ticketing when the site is being iframed and the gateway is Mastercard
		if (window.self !== window.top && gateway === gateways.MASTERCARD) return false;

		return true;
	};
})();

/**
 * Return a fallback url to be used in case of problem during the ticketing.
 *
 * @param {!Theater} theater - the theater of the screening
 * @param {!Screening} screening - the screening object
 * @return {string} a fallback URL
 */
export const getFallbackUrl = (theater, screening) => {
	let fallbackUrl = theater.website;
	const { urls } = screening;

	if (Object.keys(urls).length > 0) {
		for (const providerName of deeplinkProviders) {
			const url = urls[providerName];
			if (url) {
				fallbackUrl = url;
				break;
			}
		}
	}

	return fallbackUrl;
};

/**
 * @global
 * @typedef {Object} TicketType
 * @property {string} id - the ID of the ticket type
 * @property {string} description - the name of the type of the ticket
 * @property {string} areaCategoryCode - the ID of the area category
 */

/**
 * @global
 * @typedef {Object} AreaCategoryDef
 * @property {string} areaCategoryCode - the ID of the area category
 * @property {Array.<string>} descriptions - an array of all the ticket types names in this area
 */

/**
 * Return an array of all the different area categories found.
 *
 * @param {!Array.<TicketType>} ticketTypes - the array of all ticket types
 * @return {!Array.<AreaCategoryDef>} an array of area category definitions
 */
export const getAreaCategories = (ticketTypes) => {
	const areaCategories = [];

	for (const { areaCategoryCode, description } of ticketTypes) {
		const areaCategory = areaCategories.find(category => {
			return category.areaCategoryCode === areaCategoryCode;
		});
		if (areaCategory) {
			areaCategory.descriptions.push(description);
		} else {
			areaCategories.push({ areaCategoryCode, descriptions: [description] });
		}
	}

	return areaCategories;
};

/**
 * Return the tickets reserved by the user as a formatted string.
 *
 * @param {Object} tickets - the map of tickets selected by the user
 * @param {string=} areaCategoryCode - filter the tickets in a specific area category
 * @return {string} a textual representation of the booked tickets
 */
export const getTicketsText = (tickets, areaCategoryCode) => {
	const ticketTypeStrs = [];

	for (const ticketTypeId in tickets) {
		const ticketType = tickets[ticketTypeId];
		if (!areaCategoryCode || ticketType.areaCategoryCode === areaCategoryCode) {
			ticketTypeStrs.push(`${ticketType.description} \u00D7 ${ticketType.quantity}`);
		}
	}

	return ticketTypeStrs.join(', ');
};

/**
 * Return the number of ticket selected by the user.
 *
 * @param {Object=} ticketing - the ticketing state
 * @return {!number} the number of ticket purchased by the user
 */
const getTicketCount = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;
	const tickets = ticketing.tickets.toJS();

	let ticketCount = 0;
	for (const ticketType in tickets) {
		ticketCount += tickets[ticketType].quantity;
	}

	return ticketCount;
};

/**
 * @typedef {Object.<string, TicketType>} TicketsMap
 */

/**
 * Return the number of seat selectable by the user.
 *
 * @param {!Object.<string, TicketType>} tickets - the ticketing state
 * @return {!number} the numbe of seat selectable by the user
 */
const getTicketWithSeatCount = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;
	const tickets = ticketing.tickets.toJS();

	let seatCount = 0;
	for (const ticketType in tickets) {
		if (tickets[ticketType].isAllocatedSeating) {
			seatCount += tickets[ticketType].quantity;
		}
	}

	return seatCount;
};

/**
 * Return whether two seats position are equal.
 *
 * @function
 * @param {!SeatPosition} seatPos1 - the first seat position
 * @param {!SeatPosition} seatPos2 - the second seat position
 * @return {!boolean} true if the seat positions are equal, false otherwise
 */
export const areSeatPositionsEqual = (seatPos1, seatPos2) => {
	return seatPos1.areaNumber === seatPos2.areaNumber &&
		seatPos1.rowIndex === seatPos2.rowIndex &&
		seatPos1.columnIndex === seatPos2.columnIndex;
};

/**
 * Return whether two seats objects reference the same seat.
 *
 * @function
 * @param {!Seat} seat1 - the first seat
 * @param {!Seat} seat2 - the second seat
 * @return {!boolean} true if the seats are equal, false otherwise
 */
export const areSeatsEqual = (seat1, seat2) => {
	return areSeatPositionsEqual(seat1.position, seat2.position);
};

/**
 * Return whether a seat is a wheelchair.
 *
 * @param {!Seat} seat - the seat to test
 * @return {!boolean} true if the seat is a wheelchair, false otherwise
 */
export const isWheelchair = (seat) => (
	seat.status === seatStatus.WHEELCHAIR || seat.originalStatus === seatStatus.WHEELCHAIR
);

/**
 * Return whether a seat is a companion seat.
 *
 * @param {!Seat} seat - the seat to test
 * @return {!boolean} true if the seat is a companion seat, false otherwise
 */
export const isCompanionSeat = (seat) => (
	seat.status === seatStatus.COMPANION || seat.originalStatus === seatStatus.COMPANION
);

/**
 * Return whether the seat is reserved for a disabled person/companion.
 *
 * @function
 * @param {!Seat} seat - the seat to test
 * @return {!boolean} true if the seat is a wheelchair or a companion seat, false otherwise
 */
export const isSeatForDisablePeople = (seat) => isWheelchair(seat) || isCompanionSeat(seat);

/**
 * Return whether all seats in a array are adjacent ot each other.
 *
 * @function
 * @param {!Array.<SeatPosition>} positions - an array of seat positions
 * @return {!boolean} true if all seat positions are contiguous
 */
const areSeatPositionsContiguous = (positions) => {
	const count = positions.length;
	if (count < 2) return true;

	const areaNumber = positions[0].areaNumber;
	const rowIndex = positions[0].rowIndex;

	// check area and row
	for (let i = 0; i < count; i++) {
		const position = positions[i];
		if (position.areaNumber !== areaNumber || position.rowIndex !== rowIndex) return false;
	}

	// check contiguity
	const columnIndices = positions.map(({ columnIndex }) => columnIndex);
	columnIndices.sort((a, b) => a - b);
	for (let i = 1; i < count; i++) {
		if (columnIndices[i] !== columnIndices[i - 1] + 1) return false;
	}

	return true;
};

/**
 * @typedef {Function} BinaryComparator
 * @param {!*} element - the element to compare
 * @param {*=} objective - the objective to reach
 * @return {!number} a negative if the objective is in the upper bound, a positive for the lower bound
 */

/**
 * Perform a binary search on an array of element to reach the given objective
 *
 * @function
 * @param {!Array.<*>} array - an array of elements to search in
 * @param {!BinaryComparator} compare - a binary search comparator
 * @param {*=} objective - an optional parameter to the comparator
 * @return {*} the element, if found
 */
const binarySearch = (array, compare, objective) => {
	let minIndex = 0;
	let maxIndex = array.length - 1;
	let currentIndex;

	while (minIndex <= maxIndex) {
		currentIndex = (minIndex + maxIndex) >> 1;
		const result = compare(array[currentIndex], objective);
		if (result < 0) {
			minIndex = currentIndex + 1;
		} else if (result > 0) {
			maxIndex = currentIndex - 1;
		} else {
			return array[currentIndex];
		}
	}

	return null;
};

/**
 * Compare a seat with a column index to perform a binary comparison.
 *
 * @function
 * @type {BinaryComparator}
 * @param {!Seat} seat - the seat to test
 * @param {!number} columnIndex - the column index we're looking for
 * @return {!number} a negative if the objective is in the upper bound, a positive for the lower bound
 */
const compareSeatAtPosition = (seat, columnIndex) => seat.position.columnIndex - columnIndex;

/**
 * Return the seat at the given position from a given seat layout.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout, as defined in the MX API
 * @param {!SeatPosition} seatPosition - the position of the seat to get
 * @return {Seat} the seat from the layout at the given position, if it exists
 */
const getSeatAtPositionLayout = (seatLayout, seatPosition) => {
	const { areaNumber, rowIndex, columnIndex } = seatPosition;

	const area = seatLayout.areas.find(({ number }) => number === areaNumber);
	if (!area) return null;

	const { rows } = area;
	let row;
	// Attempt to guess the row in constant time
	if (rowIndex > 0 && rows.length > rowIndex && rows[rowIndex].seats.length > 0 &&
		rows[rowIndex].seats[0].position.rowIndex === rowIndex) {
		row = rows[rowIndex];
	} else {
		row = rows.find(({ seats }) => seats.length > 0 && seats[0].position.rowIndex === rowIndex);
	}
	if (!row) return null;

	// We'll rarely be able to find the seat in constant time, it's not worth trying
	return binarySearch(row.seats, compareSeatAtPosition, columnIndex);
};

/**
 * @global
 * @typedef {Array.<Array.<Array.<Seat>>>} SeatLookup
 */

/**
 * Return a seat lookup from a seat layout, that can be used to speed us seat search.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout, as defined in the MX API
 * @return {!SeatLookup} the corresponding seat lookup structure
 */
export const createSeatLookup = (seatLayout) => {
	const lookup = [];
	lookup.isSeatLookup = true;

	// Create the structure
	const { areas } = seatLayout;
	for (const { rowCount, columnCount } of areas) {
		const areaLookup = [];
		for (let i = 0; i < rowCount; i++) {
			const rowLookup = [];
			for (let j = 0; j < columnCount; j++) {
				rowLookup.push(null);
			}
			areaLookup.push(rowLookup);
		}
		lookup.push(areaLookup);
	}
	// Fill in the seats
	for (const { rows } of areas) {
		for (const { seats } of rows) {
			for (const seat of seats) {
				const { areaNumber, rowIndex, columnIndex } = seat.position;
				lookup[areaNumber - 1][rowIndex][columnIndex] = seat;
			}
		}
	}

	return lookup;
};

/**
 * Return the seat at the given position from a given seat layout.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout, as defined in the MX API
 * @param {!SeatPosition} seatPosition - the position of the seat to get
 * @return {Seat} the seat from the layout at the given position, if it exists
 */
const getSeatAtPositionLookup = (seatLookup, seatPosition) => {
	const areaLookup = seatLookup[seatPosition.areaNumber - 1];
	if (!areaLookup) return null;

	const rowLookup = areaLookup[seatPosition.rowIndex];
	if (!rowLookup) return null;

	return rowLookup[seatPosition.columnIndex] || null;
};

/**
 * Return the seat at the given position from a given seat layout.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout, as defined in the MX API
 * @param {!SeatPosition} seatPosition - the position of the seat to get
 * @return {Seat} the seat from the layout at the given position, if it exists
 */
export const getSeatAtPosition = (seatLayout, seatPosition) => {
	if (seatLayout.lookup && seatLayout.lookup.isSeatLookup) {
		return getSeatAtPositionLookup(seatLayout.lookup, seatPosition);
	}
	return getSeatAtPositionLayout(seatLayout, seatPosition);
};

/**
 * Return the first wheelchair found in an array of seat positions.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout, as defined in the MX API
 * @param {!Array.<SeatPosition>} seatsInGroup - array of seat positions in a disabled seats group
 * @return {Seat} the wheelchair of the group, or null if not found
 */
export const findWheelchairInGroup = (seatLayout, seatsInGroup) => {
	if (!Array.isArray(seatsInGroup)) {
		console.error('ticketingPublic.js: findWheelchairInGroup(): seatsInGroup is not an array',
			seatsInGroup);
		return null;
	}
	if (seatsInGroup.length === 0) return null;

	for (const seatPosition of seatsInGroup) {
		const seat = getSeatAtPosition(seatLayout, seatPosition);
		if (seat && isWheelchair(seat)) {
			return seat;
		}
	}

	return null;
};

/**
 * Return whether the given seat is a sofa.
 *
 * @function
 * @param {!Seat} seat - the seat to test
 * @param {!SeatLayout} seatLayout - the seat layout, as defined in the MX API
 * @return {!boolean} true if the given seat is a sofa, false otherwise
 */
export const isSofa = (seat, seatLayout) => {
	const { seatsInGroup } = seat;
	if (seatsInGroup.length < 2) return false;
	if (isWheelchair(seat)) return false;
	if (isCompanionSeat(seat) && findWheelchairInGroup(seatLayout, seatsInGroup)) return false;
	return areSeatPositionsContiguous(seatsInGroup);
};

/**
 * @global
 * @typedef {Object} SelectedSeat
 * @property {string} rowName - the label of the row containing the seat
 * @property {string} seatName - the label of the seat selected
 * @property {number} ticketId - the identifier of the ticket used to reserve this seat
 */

/**
 * @global
 * @typedef {Object} GroupedTicket
 * @property {string} ticketTypeId - the ID of the ticket type
 * @property {string} ticketTypeDescription - the name of the type of the ticket
 * @property {number} priceEach - the price for one ticket of this type
 * @property {number} quantity - the number of ticket of this type
 * @property {Array.<number>} ticketIds - the list of ticket identifiers in this group
 */

/**
 * @global
 * @typedef {Object} TicketingSeat
 * @property {string} areaCategoryCode - the ID of the area category
 * @property {string} label - the full label of the seat
 * @property {string} rowName - the name of the seat row
 * @property {string} seatName - the name of the seat
 */

/**
 * Return an array of selected seat labels per area category.
 *
 * @function
 * @param {!Array.<SelectedSeat>} selectedSeats - the array of selected seats, as returned by MX
 * @param {!Array.<GroupedTicket>} groupedTickets - the array of grouped tickets, as returned by MX
 * @param {!Array.<TicketType>} ticketTypes - the array of ticket types, as returned by MX
 * @return {!Array.<TicketingSeat>} an array of selected seat
 */
export const getSeats = (selectedSeats, groupedTickets, ticketTypes) => {
	const seats = [];

	for (const selectedSeat of selectedSeats) {
		const { rowName, seatName, ticketId } = selectedSeat;

		const areaCategory = groupedTickets.find(({ ticketIds }) => ticketIds.indexOf(ticketId) > -1);
		if (areaCategory) {
			const { ticketTypeId } = areaCategory;

			const ticketType = ticketTypes.find(({ id }) => id === ticketTypeId);
			if (ticketType) {
				seats.push({
					areaCategoryCode: ticketType.areaCategoryCode,
					label: rowName + seatName,
					rowName,
					seatName
				});
			} else {
				console.warn('ticketingPublic:getSeats(): Could not find ticket type for ' +
					'the area category:', areaCategory, 'in:', ticketTypes);
			}
		} else {
			console.warn('ticketingPublic:getSeats(): Could not find the grouped ' +
				'tickets for the selected seat:', selectedSeat, 'in:', groupedTickets);
		}
	}

	seats.sort((seat1, seat2) => {
		const { areaCategoryCode: area1 } = seat1;
		const { areaCategoryCode: area2 } = seat2;
		if (area1 !== area2) {
			return area1.localeCompare(area2, undefined, { numeric: true });
		}
		if (seat1.rowName !== seat2.rowName) {
			return seat1.rowName.localeCompare(seat2.rowName);
		}
		return seat1.seatName - seat2.seatName;
	});

	return seats;
};

/**
 * @global
 * @typedef {Object} PickedSeat
 * @property {Seat} seat - the MX structure holding the seat
 * @property {number} lastAction - the timestamp of the last move for this seat
 */

/**
 * Return whether all the seats of a sofa are picked.
 *
 * @param {!Array.<SeatPosition>} seatsInGroup - position of each seat in the sofa
 * @param {!Array.<PickedSeat>} pickedSeat - the array of picked seats
 * @return {!boolean} true if all seats are picked, false otherwise
 */
const areAllSeatsInSofaPicked = (seatsInGroup, pickedSeats) => {
	for (const seatPosition of seatsInGroup) {
		const pickedSeat = pickedSeats.find((pickedSeat) => {
			return areSeatPositionsEqual(pickedSeat.seat.position, seatPosition);
		});
		if (!pickedSeat) return false;
	}
	return true;
};

/**
 * Remove every seat of a sofa from the array of picked seats.
 *
 * @param {!Array.<SeatPosition>} seatsInGroup - position of each seat in the sofa
 * @param {!Array.<PickedSeat>} pickedSeats - the array of picked seats
 */
const removeSofaFromPickedSeats = (seatsInGroup, pickedSeats) => {
	for (const seatPosition of seatsInGroup) {
		const index = pickedSeats.findIndex((pickedSeat) => {
			return areSeatPositionsEqual(pickedSeat.seat.position, seatPosition);
		});
		if (index > -1) pickedSeats.splice(index, 1);
	}
};

/**
 * Handler used for sorting an array of picked seats.
 *
 * @function
 * @param {PickedSeat} pickedSeat1 - the first picked seat
 * @param {PickedSeat} pickedSeat2 - the second picked seat
 * @return {number} how to sort the given picked seats.
 */
const pickedSeatsSorterStable = (pickedSeat1, pickedSeat2) => {
	const pos1 = pickedSeat1.seat.position;
	const pos2 = pickedSeat2.seat.position;

	let diff = pos1.areaNumber - pos2.areaNumber;
	if (diff !== 0) return diff;

	diff = pos1.rowIndex - pos2.rowIndex;
	if (diff !== 0) return diff;

	// NOTE: column indices are in reverse order
	return pos2.columnIndex - pos1.columnIndex;
};

/**
 * Return the array of picked seats from the given seat layout.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout, as defined in the MX API
 * @param {boolean=} removeIncompleteSofas - whether to discard incompletely picked sofas
 * @param {boolean=} sort - whether to sort the picked seats array
 * @return {!Array.<PickedSeat>} the array of picked seats taken from the seating map
 */
export const getPickedSeats = (seatLayout, removeIncompleteSofas = true, sort = true) => {
	const pickedSeats = [];
	const lastAction = Date.now();

	for (const areaCategory of seatLayout.areaCategories) {
		for (const seatPosition of areaCategory.selectedSeats) {
			const seat = getSeatAtPosition(seatLayout, seatPosition);
			if (seat) {
				pickedSeats.push({ seat, lastAction });
			} else {
				console.warn('ticketingPublic:getPickedSeats(): Could not find seat at position:',
					seatPosition, 'in layout:', seatLayout);
			}
		}
	}

	if (removeIncompleteSofas) {
		// Remove any picked sofa that doesn't have all its seats picked
		const sofas = new Set();
		// TODO: The Set is useless in this case because all sofa arrays have independent refs
		for (const { seat } of pickedSeats) {
			if (isSofa(seat, seatLayout)) sofas.add(seat.seatsInGroup);
		}
		for (const sofa of sofas) {
			if (!areAllSeatsInSofaPicked(sofa, pickedSeats)) {
				removeSofaFromPickedSeats(sofa, pickedSeats);
			}
		}
	}

	if (sort) {
		pickedSeats.sort(pickedSeatsSorterStable);
	}

	return pickedSeats;
};

/**
 * @global
 * @typedef {Object} SeatBoundingBox
 * @property {number} left - the leftmost position in the seating areas
 * @property {number} right - the rightmost position in the seating areas
 * @property {number} top - the topmost position in the seating areas
 * @property {number} bottom - the bottommost position in the seating areas
 */

/**
 * Return the actual bounding box of the given seat layout.
 * The bounding box returned by MX does include:
 * <ul>
 *   <li>space between the screen and the seating areas</li>
 *   <li>empty seating rows</li>
 *   <li>screen width</li>
 * </ul>
 * The bounding box returned by this function only includes the seating area.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout, as returned by MX
 * @return {!SeatBoundingBox} the bounding box of the seating areas
 */
export const getSeatLayoutBoundingBox = (seatLayout) => {
	const box = {
		left:   100.0,
		right:  0.0,
		top:    100.0,
		bottom: 0.0
	};

	seatLayout.areas.forEach((area) => {
		const { left, top, width, height, columnCount, rowCount, rows } = area;
		const cellWidth = width / columnCount;
		const cellHeight = height / rowCount;

		for (let i = 0, il = rows.length; i < il; i++) {
			const { seats } = rows[i];
			for (let j = 0, jl = seats.length; j < jl; j++) {
				const { columnIndex, rowIndex } = seats[j].position;
				box.left = Math.min(left + columnIndex * cellWidth, box.left);
				box.right = Math.max(left + (columnIndex + 1) * cellWidth, box.right);
				box.top = Math.min(top + (rowIndex) * cellHeight, box.top);
				box.bottom = Math.max(top + (rowIndex + 1) * cellHeight, box.bottom);
			}
		}
	});

	// Clip the bounding box in case of incorrect size
	if (box.left > box.right) box.left = box.right;
	if (box.top > box.bottom) box.top = box.bottom;

	return box;
};

/**
 * Return whether the seats of a seat layout have a unique name.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout, as returned by MX
 * @return {!boolean} true if each seat has a unique name, false otherwise
 */
export const hasUniqueSeatLabels = (seatLayout) => {
	const set = new Set();
	const { areas } = seatLayout;

	for (let i = 0, il = areas.length; i < il; i++) {
		const { rows } = areas[i];

		for (let j = 0, jl = rows.length; j < jl; j++) {
			const { seats } = rows[j];

			for (let k = 0, kl = rows.length; k < kl; k++) {
				if (seats[k]) {
					const { seatLabel } = seats[k];

					if (set.has(seatLabel)) return false;
					set.add(seatLabel);
				}
			}
		}
	}

	return true;
};

/**
 * Return the URL of the "Print your ticket" page.
 *
 * @return {string} the URL of the showtime ticket.
 */
const getRelativeUrl = (pageSlug) => {
	const store = Store.get();

	const page = accessNested(store, ['appData', 'pages', pageSlug], '');
	const activePage = accessNested(store, 'activePage');
	const activePath = getRelativeRoot(activePage.path);

	// On staging https, we need to append index.html
	const appendIndex = window.location.pathname.split('/').pop() === 'index.html';

	// NOTE: We don't need to populate the  parameter
	//       because the iframe communicator doesn't have any geo fencing

	let url = joinPaths(pathOnly(window.location.href), activePath, page.path);
	if (appendIndex) url = joinPaths(url, 'index.html');

	return url;
};

/**
* Return the URL of the payment notification page.
*
* @return {string} the URL to give to payment gateways as a callback.
*/
export const getIframeCommunicatorUrl = () => getRelativeUrl(IFRAME_COMMUNICATOR_PAGE_SLUG);
if (typeof window !== 'undefined') window.getIframeCommunicatorUrl = getIframeCommunicatorUrl;

/**
 * Return the URL of the "Print your ticket" page.
 *
 * @return {string} the URL of the showtime ticket.
 */
export const getPrintYourTicketUrl = () => getRelativeUrl(PRINT_YOUR_TICKET_PAGE_SLUG);

/**
 * Return whether the iframe communicator is ready.
 *
 * @return {Promise} true if the iframe communicator is ready, false otherwise.
 */
export const checkIframeCommunicatorUrl = (() => {
	let prom_;
	return function () {
		if (!prom_) {
			prom_ = fetch(getIframeCommunicatorUrl()).then(res => {
				if (res.status !== 200 && res.status !== 304) {
					throw new Error('Error while getting iframe coomunicator');
				}
				return res.text();
			}).then(res => {
				if (res.indexOf('PAYMENT_MESSAGE_RECEIVED') === -1) {
					throw new Error('Incorrect setup for iframe communicator');
				}
			});
		}
		return prom_;
	};
})();

/**
 * Generate the card number placeholder to be used in payment forms.
 * This function is here to prevent security softwares from flagging raw card numbers as an issue.
 *
 * @return {!string} the card number placeholder
 */
export const generateCardNumPlaceholder = () => {
	const a = [];
	for (let i = 0; i < 19; i++) {
		a[i] = (i + 1) % 5 === 0 ? 0x20 : 0x31 + 3 * (i === 0);
	}
	return String.fromCharCode.apply(null, a);
};
